import React, { useEffect, useState } from 'react';
import { Fingerprint, KeyRound, Link2, ShieldAlert, Activity } from 'lucide-react';
import { InventoryPage } from '../inventory/InventoryChrome';
import { Card, CardHeader, Note, RubyModal } from '../../components/finance/financeUi';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import {
  identityAdminApi, type IdentitySettings, type IdentityStatus,
} from '../../services/identityAdminApi';
import { useStaffAccess } from '../../hooks/useStaffAccess';

function keyStatusLine(settings: IdentitySettings | null): string {
  if (!settings) return 'Loading…';
  if (settings.keyKind === 'restricted') return 'Restricted key set';
  if (settings.keyKind === 'fallback') return 'Using shared test key — date of birth and ID numbers unavailable';
  return 'Not set';
}

function errorMessage(e: any, fallback: string): string {
  const code = e?.message || '';
  if (code === 'wrong_mode') return 'This key does not match the stage (prod needs rk_live_, dev/staging need rk_test_).';
  if (code === 'invalid_key') return 'Key must start with rk_test_ or rk_live_.';
  if (code === 'key_rejected') return 'Stripe rejected that key.';
  if (code === 'key_missing') return 'Set a Stripe key before registering the webhook.';
  return code || fallback;
}

/** A write-only secret field: never pre-filled, cleared immediately after a successful save. */
const StripeKeyCard: React.FC<{
  settings: IdentitySettings | null;
  canEdit: boolean;
  onSave: (value: string) => Promise<void>;
}> = ({ settings, canEdit, onSave }) => {
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!value.trim()) { setError('Enter a value to replace it.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave(value.trim());
      setValue('');
      setEditing(false);
    } catch (e: any) {
      setError(errorMessage(e, 'Could not save the key'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-stone-800 flex items-center gap-1.5">
            <KeyRound size={14} /> Stripe restricted key
          </div>
          <div className="text-xs text-stone-500 mt-0.5">{keyStatusLine(settings)}</div>
        </div>
        {!editing && canEdit && (
          <button
            type="button"
            onClick={() => { setEditing(true); setValue(''); setError(null); }}
            className="px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 min-h-[44px]"
          >
            Replace
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="rk_test_… or rk_live_… — never pre-filled"
            className="flex-1 min-w-[220px] px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-800 outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[44px]"
          />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg bg-black text-white text-xs font-semibold hover:bg-stone-900 disabled:opacity-40 min-h-[44px]"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setValue(''); setError(null); }}
            className="px-4 py-2.5 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 min-h-[44px]"
          >
            Cancel
          </button>
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </Card>
  );
};

/** Custom on/off toggle — no native checkbox/switch. */
/** The clickable button is >=44x44 (touch target rule); the visual track inside it
 * stays the original 48x28 size — only the hit area grows, never the look. */
const Toggle: React.FC<{ on: boolean; disabled?: boolean; onChange: () => void; label: string }> = ({ on, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className="relative inline-flex items-center justify-center shrink-0 disabled:opacity-40"
    style={{ minWidth: 44, minHeight: 44 }}
  >
    <span
      className={`relative inline-flex items-center w-12 h-7 rounded-full transition-colors ${on ? 'bg-[#DC143C]' : 'bg-stone-300'}`}
      style={{ minWidth: 48, minHeight: 28 }}
    >
      <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </span>
  </button>
);

const IdentityVerificationSettings: React.FC = () => {
  const { hasAccess, levelForPath } = useStaffAccess();
  const canEdit = levelForPath('/identity-admin') === 'edit';

  const [settings, setSettings] = useState<IdentitySettings | null>(null);
  const [status, setStatus] = useState<IdentityStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [webhookBusy, setWebhookBusy] = useState(false);
  const [webhookConfirm, setWebhookConfirm] = useState(false);
  const [webhookError, setWebhookError] = useState<string | null>(null);

  const [toggleBusy, setToggleBusy] = useState(false);

  const [consentDraft, setConsentDraft] = useState('');
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentConfirm, setConsentConfirm] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([identityAdminApi.settings(), identityAdminApi.status()])
      .then(([s, st]) => { setSettings(s); setStatus(st); })
      .catch((e) => setError(e?.message || 'Could not load ID Verification settings'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const saveKey = async (key: string) => {
    const r = await identityAdminApi.setStripeKey(key);
    setSettings((prev) => (prev ? { ...prev, keySet: true, keyKind: r.keyKind } : prev));
  };

  const registerWebhook = async () => {
    setWebhookBusy(true);
    setWebhookError(null);
    try {
      const r = await identityAdminApi.registerWebhook();
      setSettings((prev) => (prev ? { ...prev, webhookSet: r.webhookSet } : prev));
      setWebhookConfirm(false);
    } catch (e: any) {
      setWebhookError(errorMessage(e, 'Could not register the webhook'));
    } finally {
      setWebhookBusy(false);
    }
  };

  const toggleRedact = async () => {
    if (!settings || toggleBusy) return;
    setToggleBusy(true);
    try {
      const next = await identityAdminApi.updateSettings({ redactAfterStore: !settings.redactAfterStore });
      setSettings((prev) => (prev ? { ...prev, ...next } : prev));
    } catch (e: any) {
      setError(errorMessage(e, 'Could not update the setting'));
    } finally {
      setToggleBusy(false);
    }
  };

  const publishConsentVersion = async () => {
    if (!consentDraft.trim()) { setConsentError('Enter a version string.'); return; }
    setConsentBusy(true);
    setConsentError(null);
    try {
      const next = await identityAdminApi.updateSettings({ consentVersion: consentDraft.trim() });
      setSettings((prev) => (prev ? { ...prev, ...next } : prev));
      setConsentConfirm(false);
      setConsentDraft('');
    } catch (e: any) {
      setConsentError(errorMessage(e, 'Could not publish the new version'));
    } finally {
      setConsentBusy(false);
    }
  };

  if (!hasAccess('/identity-admin')) return null;

  return (
    <InventoryPage icon={Fingerprint} title="ID Verification" subtitle="The Stripe key, webhook and consent settings identity checks run on" crumb="Settings">
      {!canEdit && <Note tone="info">You can view these settings; only an editor for ID Verification can change them.</Note>}
      {error && <Note tone="danger">{error}</Note>}

      {loading || !settings ? (
        <RubyLoader variant="page" label="Loading ID Verification settings" />
      ) : (
        <div className="space-y-4">
          <StripeKeyCard settings={settings} canEdit={canEdit} onSave={saveKey} />

          <Card>
            <CardHeader
              icon={<Link2 size={16} />}
              title="Webhook"
              hint={settings.webhookSet ? 'Registered' : 'Not registered'}
              actions={canEdit ? (
                <button
                  type="button"
                  onClick={() => setWebhookConfirm(true)}
                  disabled={webhookBusy}
                  className="px-3 py-2 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-black disabled:opacity-40 min-h-[44px]"
                >
                  {webhookBusy ? 'Working…' : 'Register webhook'}
                </button>
              ) : undefined}
            />
            <div className="p-4 text-sm text-stone-700">
              Status: {settings.webhookSet ? 'set' : 'not set'}
            </div>
            {webhookError && <div className="px-4 pb-4 text-xs text-red-600">{webhookError}</div>}
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-stone-800">Delete our copy at Stripe after storing</div>
                <div className="text-xs text-stone-500 mt-0.5">
                  When on, Stripe's copy of the verification session is redacted once we have stored the result.
                </div>
              </div>
              <Toggle
                on={settings.redactAfterStore}
                disabled={!canEdit || toggleBusy}
                onChange={toggleRedact}
                label="Delete our copy at Stripe after storing"
              />
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-sm font-semibold text-stone-800">Consent version</div>
            <div className="text-xs text-stone-500 mt-0.5">Current: {settings.consentVersion || 'Not set'}</div>
            {canEdit && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={consentDraft}
                  onChange={(e) => setConsentDraft(e.target.value)}
                  placeholder="New consent version"
                  className="flex-1 min-w-[180px] px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-800 outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[44px]"
                />
                <button
                  type="button"
                  onClick={() => setConsentConfirm(true)}
                  disabled={!consentDraft.trim()}
                  className="px-4 py-2.5 rounded-lg bg-black text-white text-xs font-semibold hover:bg-stone-900 disabled:opacity-40 min-h-[44px]"
                >
                  Publish new version
                </button>
              </div>
            )}
          </Card>

          <Card>
            <CardHeader icon={<Activity size={16} />} title="Status" />
            <div className="p-4 grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                {status && status.dlqDepth > 0 ? (
                  <ShieldAlert size={14} className="text-[#DC143C]" />
                ) : null}
                <span className={status && status.dlqDepth > 0 ? 'text-[#DC143C] font-semibold' : 'text-stone-700'}>
                  DLQ depth: {status ? status.dlqDepth : '—'}
                </span>
              </div>
              <div className="text-stone-700">
                Open checks: {status && status.openChecks !== null ? status.openChecks : '—'}
              </div>
            </div>
          </Card>
        </div>
      )}

      <RubyModal
        open={webhookConfirm}
        icon={<Link2 size={16} style={{ color: '#DC143C' }} />}
        title="Register the identity webhook?"
        confirmLabel="Register webhook"
        busy={webhookBusy}
        onClose={() => setWebhookConfirm(false)}
        onConfirm={registerWebhook}
      >
        <p>Stripe will be told where to send identity verification events for this stage.</p>
        {settings?.webhookSet && <p className="text-amber-700 font-medium">A webhook is already registered — this replaces it.</p>}
      </RubyModal>

      <RubyModal
        open={consentConfirm}
        icon={<Fingerprint size={16} style={{ color: '#DC143C' }} />}
        title="Publish a new consent version?"
        confirmLabel="Publish"
        busy={consentBusy}
        onClose={() => setConsentConfirm(false)}
        onConfirm={publishConsentVersion}
      >
        <p>New checks will require consent to version <strong>{consentDraft}</strong>.</p>
        {consentError && <p className="text-red-600">{consentError}</p>}
      </RubyModal>
    </InventoryPage>
  );
};

export default IdentityVerificationSettings;
